import type { QrCodeProps } from "../types";

/**
 * The backend fills `{{tokens}}` with Handlebars' default (HTML-escaping)
 * output, so a calendar link like `?a=1&b=2` reaches the viewer as
 * `?a&#x3D;1&amp;b&#x3D;2`. Decode the escapes Handlebars emits before the
 * value is encoded, or the phone opens a broken link.
 */
const HANDLEBARS_ESCAPES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#x27;": "'",
  "&#39;": "'",
  "&#x60;": "`",
  "&#x3D;": "=",
};

export const decodeHandlebarsEscapes = (value: string) =>
  value.replace(
    /&(?:amp|lt|gt|quot|#x27|#39|#x60|#x3D);/g,
    (match) => HANDLEBARS_ESCAPES[match] ?? match,
  );

/** A token the backend has not replaced (template page, missing value). */
export const isUnresolvedToken = (value: string) =>
  value.includes("{{") || value.includes("[[");

const clean = (value: unknown) =>
  typeof value === "string" ? decodeHandlebarsEscapes(value).trim() : "";

/**
 * The published page's own link. Version pages carry the short link in a
 * global the backend fills at version creation; a page without one (the story
 * itself, an older version) falls back to the address it was opened at.
 *
 * The page templates (rendering-provider/templates, and the backend's
 * v4-canonical-template) emit `__SD_SHORT_URL__` for every org; the engagement
 * bar's share link reads it too. Like the bar, only an https value counts: the
 * placeholder stays unfilled in previews.
 */
export const getRuntimeVersionUrl = (pdfShortUrl?: string): string => {
  if (pdfShortUrl) return pdfShortUrl;
  if (typeof window === "undefined") return "";
  const shortUrl = (window as unknown as { __SD_SHORT_URL__?: unknown })
    .__SD_SHORT_URL__;
  if (typeof shortUrl === "string" && shortUrl.startsWith("https://")) {
    return shortUrl;
  }
  return `${window.location.origin}${window.location.pathname}`;
};

/**
 * What a live deck encodes. When the chosen source has nothing scannable (a
 * variable the story page or Present has no value for, an empty field), the
 * code falls back to the deck's own link, so the design always shows and a
 * scan still lands somewhere sensible.
 */
export const resolveQrValue = (
  settings: QrCodeProps,
  runtimeVersionUrl: () => string,
): string => {
  let value = "";
  switch (settings.linkType) {
    case "variable":
      value = clean(settings.urlVariable);
      break;
    case "version":
      value = runtimeVersionUrl();
      break;
    case "existing-version":
      value = clean(settings.versionUrl);
      break;
    default:
      value = clean(settings.url);
  }
  if (!value || isUnresolvedToken(value)) value = runtimeVersionUrl();
  return toHref(value);
};

const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/i;
// What a click may open. Any other value (a `javascript:` URL from a variable
// or the `storyShortUrl` query param) still draws but gets no link: the
// viewer's React 18 does not block javascript: hrefs.
const CLICKABLE = /^(?:https?|mailto|tel):/i;

export const isClickableHref = (value: string) => CLICKABLE.test(value);
const EMAIL = /^[^\s@:/]+@[^\s@/]+\.[a-z]{2,}$/i;
// Digits with the usual separators and an optional leading +, no letters.
const PHONE = /^\+?[\d\s().-]+$/;
const BARE_DOMAIN = /^[^\s/]+\.[a-z]{2,}(?:[/?#]|$)/i;

/**
 * Turns what a field or variable holds into something a phone camera opens.
 * A variable can hold a website, an email or a phone number whatever it is
 * called (custom ones too), so the value decides: an email gets `mailto:`, a
 * phone number `tel:`, a bare domain ("calendly.com/me") `https://`. A value
 * with a scheme already, or text that is none of these, is encoded as typed.
 */
export const toHref = (value: string) => {
  if (HAS_SCHEME.test(value)) return value;
  if (EMAIL.test(value)) return `mailto:${value}`;
  if (PHONE.test(value)) {
    const digits = value.replace(/\D/g, "");
    // 7–15 digits: real numbers (E.164 caps at 15), not a year or a code.
    if (digits.length >= 7 && digits.length <= 15) {
      return `tel:${value.trim().startsWith("+") ? "+" : ""}${digits}`;
    }
  }
  if (BARE_DOMAIN.test(value)) return `https://${value}`;
  return value;
};
